import React, { useCallback } from 'react';
import { Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { gql, useMutation } from 'urql';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import { CreateLeagueMutation, CreateLeagueMutationVariables } from '../../generated/hooks';
import { CreateLeagueInputType, LeagueType, WeekZeroType } from '../../generated/schemas';
import { creatableLeagueTypeOptions, weekZeroTypeOptions } from '../../utils';
import InputField from '../forms/InputField';
import NumericField from '../forms/NumericField';
import SelectField from '../forms/SelectField';

const createLeague = gql`
  mutation createLeague($createLeagueInput: CreateLeagueInputType!) {
    createLeague(input: $createLeagueInput) {
      ok
      league {
        id
        name
        season
        leagueType
        userIsCommissioner
        chatEnabled
        commissioner {
          id
          fullName
        }
        invitations {
          id
          email
          user {
            id
            fullName
          }
          status
        }
        members {
          id
          user {
            id
            fullName
          }
        }
      }
    }
  }
`;

type CreateLeagueModalProps = {
  isSuperuser: boolean;
  onClose: () => void;
};

function CreateLeagueModal({ isSuperuser, onClose }: CreateLeagueModalProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      leagueName: '',
      leagueType: LeagueType.PickEm,
      buyInAmount: '',
      weekZeroType: WeekZeroType.Ignore,
      chatEnabled: false,
      invitationEmails: [] as { value: string }[],
    },
  });
  const invitationEmails = useFieldArray({ control, name: 'invitationEmails' });
  const leagueType = watch('leagueType');

  const { flashMutationError } = useFlashMessageContext();
  const [, createLeagueMutation] = useMutation<CreateLeagueMutation, CreateLeagueMutationVariables>(createLeague);
  const handleCreateLeague = useCallback(
    (createLeagueInput: CreateLeagueInputType) => createLeagueMutation({ createLeagueInput }),
    [createLeagueMutation],
  );
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isSuperuser ? 'Create League' : "Create Pick 'Em League"}</DialogTitle>
      <form
        onSubmit={handleSubmit(
          ({ leagueName, leagueType, buyInAmount, weekZeroType, chatEnabled, invitationEmails }) => {
            return handleCreateLeague({
              name: leagueName,
              leagueType,
              invitationEmails: invitationEmails.map(invitationEmail => invitationEmail.value),
              chatEnabled,
              ...(isSuperuser && { buyInAmount: buyInAmount === '' ? null : buyInAmount }),
              ...(isSuperuser && leagueType === LeagueType.PickEm && { weekZeroType }),
            }).then(response => {
              if (flashMutationError(response.error)) {
                return;
              }
              onClose();
            });
          },
        )}
      >
        <DialogContent>
          <Grid container spacing={2}>
            {!isSuperuser && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Stock Market leagues can only be created by the site administrator.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <InputField
                control={control}
                label="League Name"
                name="leagueName"
                rules={{ required: 'Required' }}
                sx={{ width: '100%' }}
              />
            </Grid>
            {isSuperuser && (
              <>
                <Grid item xs={12}>
                  <SelectField
                    control={control}
                    label="League Type"
                    name="leagueType"
                    options={creatableLeagueTypeOptions}
                    rules={{ required: 'Required' }}
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <NumericField control={control} label="Buy-in Amount" name="buyInAmount" sx={{ width: '100%' }} />
                </Grid>
                {leagueType === LeagueType.PickEm && (
                  <Grid item xs={12}>
                    <SelectField
                      control={control}
                      label="Week Zero"
                      name="weekZeroType"
                      options={weekZeroTypeOptions}
                      rules={{ required: 'Required' }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12}>
              <Controller
                control={control}
                name="chatEnabled"
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={<Checkbox checked={value} onChange={e => onChange(e.target.checked)} />}
                    label="Enable league chat"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mt: 2, mx: -3 }} />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ flexGrow: 1 }}>
                Invitations
              </Typography>
              <IconButton color="primary" onClick={() => invitationEmails.append({ value: '' })}>
                <AddIcon />
              </IconButton>
            </Grid>
            {invitationEmails.fields.map((field, index) => (
              <Grid key={field.id} item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <InputField
                  control={control}
                  label="Email Address"
                  name={`invitationEmails.${index}.value`}
                  rules={{ required: 'Required' }}
                  sx={{ width: '100%' }}
                />
                <IconButton color="error" onClick={() => invitationEmails.remove(index)} sx={{ ml: 1 }}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CreateLeagueModal;
